import {
  Document,
  Font,
  Page,
  pdf,
  StyleSheet,
  Text,
  View
} from "@react-pdf/renderer"
import moment from "moment"
import { BalanceSheetReportType } from "pages/Accounts/Report/ProfitAndLoss/types"
import { useHandleAuth } from "utils"
import { buildHierarchy } from "../functions"

type UseHandleExportPdf = {
  data: BalanceSheetReportType[]
  comparisonDates: string[]
}

type HandleExportPdfType = {
  date?: string
  displayAccountNumber?: boolean
  displaySubheader?: boolean
  displayDetail?: boolean
}

export const useHandleExportPdf = ({
  data,
  comparisonDates
}: UseHandleExportPdf) => {
  const mapData: any[] = []
  const auth = useHandleAuth()
  const token = auth.userTokenData()

  const parents = data.filter((item) => item.parent_id === -1)

  const mapDataHirarki = parents?.map((item) => {
    const hierarchy = buildHierarchy(data, item.coa_id)
    return {
      ...item,
      children: hierarchy
    }
  })

  makeRowsPdf(mapDataHirarki, mapData)

  return {
    handleExportPdf: ({
      date,
      displayAccountNumber,
      displaySubheader,
      displayDetail
    }: HandleExportPdfType) =>
      pdf(
        <TableBalanceSheet
          companyName={token?.company_name ?? ""}
          data={mapData}
          comparisonDates={comparisonDates}
          date={date}
          displayAccountNumber={displayAccountNumber}
          displaySubheader={displaySubheader}
          displayDetail={displayDetail}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement("a")
          link.href = url
          link.download = "balance_sheet.pdf"
          link.click()
          URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.error("Gagal membuat PDF:", error)
        })
  }
}

export const makeRowsPdf = (data: any, mapData: any[]) => {
  data.forEach((item: any) => {
    let itemRow = {
      ...item
    }

    if (item.coa_name === "NET ASSETS" || item.coa_level !== 4) {
      itemRow.bold = true
    }

    mapData.push(itemRow)

    if (item.children?.length > 0) {
      makeRowsPdf(item.children, mapData)
    }

    if (item.header_level < 3 && item.coa_name !== "NET ASSETS") {
      mapData.push({
        ...item,
        coa_name: `Total ${item.coa_name}`,
        bold: true
      })
    }
  })
}

type TableBalanceSheetProps = {
  companyName: string
  data: any[]
  date?: string
  comparisonDates: string[]
  displayAccountNumber?: boolean
  displaySubheader?: boolean
  displayDetail?: boolean
}

export const TableBalanceSheet = ({
  companyName,
  data,
  date,
  comparisonDates,
  displayAccountNumber,
  displaySubheader,
  displayDetail
}: TableBalanceSheetProps) => {
  Font.register({
    family: "Roboto",
    fonts: [
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxP.ttf" // Regular
      },
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf",
        fontWeight: "medium"
      },
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf",
        fontWeight: "bold"
      },
      {
        src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf",
        fontWeight: "ultrabold"
      }
    ]
  })

  const styles = StyleSheet.create({
    table: {
      display: "flex",
      width: "auto",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "#fff"
    },
    tableRow: {
      flexDirection: "row"
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: "#fff"
    },
    tableCol: {
      flex: 1,
      // borderBottomStyle: "solid",
      // borderBottomWidth: 1,
      // borderBottomColor: "#aeaeb4"
    },
    tableCell: {
      margin: 2,
      fontSize: 8,
      fontFamily: "Roboto"
    },
    headerText: {
      margin: 2,
      fontSize: 9,
      fontWeight: "bold",
      fontFamily: "Roboto"
    }
  })

  return (
    <Document>
      <Page
        size="A4"
        style={{
          paddingHorizontal: 15,
          paddingBottom: 35,
          paddingTop: 15,
          position: "relative"
        }}>
        <Text
          style={{
            fontSize: 22,
            marginTop: 30,
            marginBottom: 8,
            textAlign: "left",
            fontWeight: "heavy"
          }}>
          Balance Sheet
        </Text>

        <Text
          style={{
            fontSize: 14,
            marginBottom: 4,
            textAlign: "left",
            fontWeight: "extrabold"
          }}>
          {companyName?.toUpperCase() ?? ""}
        </Text>

        <Text
          style={{
            fontSize: 14,
            marginBottom: 4,
            textAlign: "left",
            fontWeight: "extrabold"
          }}>
          As at {moment(date).locale("en-us").format("DD MMMM YYYY")}
        </Text>

        <View style={[styles.table]}>
          {/* Header */}
          <View
            style={{
              ...styles.tableHeader,
              borderBottomColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: 0.5
            }}
          >
            <View style={[styles.tableCol]} />
            {comparisonDates.map((col: any, index: number) => (
              <View key={index} style={[styles.tableCol, { flex: col.width }]}>
                <Text style={[
                  styles.headerText,
                  {
                    textAlign: "right",
                    textTransform: "uppercase",
                    fontSize: 7
                  }
                ]}>
                  {moment(col).format("DD MMM YYYY")}
                </Text>
              </View>
            ))}
          </View>

          {/* Data Rows */}
          {data.map((item: any, rowIndex: number) => {
            const nextItem = data[rowIndex + 1]

            if (
              item.header_level === 3 ||
              item.coa_name === "NET ASSETS" ||
              item.coa_name.includes("Total")
            ) {
              return (
                <View wrap={false} key={rowIndex} style={[styles.tableRow]}>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        paddingBottom:
                          item.coa_name.includes("Total") &&
                          !nextItem?.coa_name?.includes("Total")
                          ? 10
                          : 0,
                        paddingLeft: (item.coa_level + (item.coa_name.includes("Total") ? 1 : 0)) * 5
                      }
                    ]}>
                    <Text
                      style={[
                        styles.tableCell,
                        {
                          textTransform: "uppercase",
                          fontSize: 8,
                          borderBottomColor: item.coa_name.includes("Total")
                            ? "#fff"
                            : "#d8d8da",
                          borderBottomStyle: "solid",
                          borderBottomWidth: 0.5,
                          fontWeight: item.coa_name.includes("Total") ? "bold" : "normal"
                        }
                      ]}>
                      {displayAccountNumber ? item.coacode : ""} {item.coa_name}
                    </Text>
                  </View>

                  {item.balance?.map((balance: number) => {
                    return (
                      <View
                        style={[
                          styles.tableCol,
                          {
                            borderBottomColor: item.coa_name.includes("Total")
                              ? "#fff"
                              : "#d8d8da",
                            paddingBottom: item.coa_name.includes("Total")
                              ? 2
                              : 0
                          }
                        ]}>
                        <Text
                          key={balance}
                          style={[
                            styles.tableCell,
                            {
                              textAlign: "right",
                              fontSize: 8,
                              borderBottomColor: item.coa_name.includes("Total")
                                ? "#fff"
                                : "#d8d8da",
                              borderBottomStyle: "solid",
                              borderBottomWidth: 0.5,
                              paddingRight: 10,
                              fontWeight: item.coa_name.includes("Total") ? "bold" : "normal"
                            }
                          ]}>
                          {balance > 0
                            ? balance.toLocaleString("id-ID")
                            : balance < 0
                              ? `(${Math.abs(balance).toLocaleString("id-ID")})`
                              : "-"}
                        </Text>
                      </View>
                    )
                  })}
                </View>
              )
            }

            return (
              <View wrap={false} key={rowIndex} style={styles.tableRow}>
                <View
                  style={[
                    styles.tableCol,
                    {
                      paddingTop: 4,
                      paddingLeft: (item.coa_level + (item.coa_name.includes("Total") ? 1 : 0)) * 5
                    }
                  ]}
                >
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        fontSize: item.coa_level === 1 ? 10 : 8,
                        fontWeight: item.bold ? "bold" : "normal",
                        borderBottomColor: item.coa_name.includes("Total")
                          ? "#fff"
                          : "#000",
                        borderBottomStyle: "solid",
                        borderBottomWidth: 0.5,
                        paddingRight: 10
                      }
                    ]}>
                    {displayAccountNumber ? item.coacode : ""} {item.coa_name}
                  </Text>
                </View>
              </View>
            )
          })}
        </View>
      </Page>
    </Document>
  )
}
